// Radha — recurring invoices / subscriptions (Zoho Billing style). Templates are
// read/created/paused here; generate_due_recurring_invoices() (migration 042)
// turns due templates into real invoices, same idiom as generate_collection_proposals.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type Cadence = "monthly" | "quarterly" | "annual";

export interface RecurringInvoice {
  id: string; customer_name: string; customer_gstin: string | null; place_of_supply: string | null;
  taxable_amount: string; tax_amount: string; cadence: Cadence; due_days: number;
  next_run_date: string; last_run_date: string | null; is_active: boolean;
}

export interface NewRecurringInvoice {
  customer_name: string; customer_gstin: string | null; place_of_supply: string | null;
  taxable_amount: number; tax_amount: number; cadence: Cadence; due_days: number; next_run_date: string;
}

const KEY = ["recurring-invoices"];

export function useRecurringInvoices() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<RecurringInvoice[]> => {
      const { data, error } = await supabase
        .from("recurring_invoices")
        .select("id, customer_name, customer_gstin, place_of_supply, taxable_amount, tax_amount, cadence, due_days, next_run_date, last_run_date, is_active")
        .order("next_run_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as RecurringInvoice[];
    },
  });
}

export function useCreateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (n: NewRecurringInvoice) => {
      const { error } = await supabase.from("recurring_invoices").insert(n);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useToggleRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("recurring_invoices").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Turns every due, active template into a real invoice. Returns count created. */
export function useGenerateDueInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("generate_due_recurring_invoices");
      if (error) throw error;
      return (data as number) ?? 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["invoicing-direct", "invoices"] });
    },
  });
}
